/* eslint-disable max-len */
import postalCodes from 'postal-codes-js';
import debug from 'debug';
import moment from 'moment';

const error = debug('validator:error');

const emailReg =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const curpReg =
  /[A-Z][A,E,I,O,U,X][A-Z]{2}[0-9]{2}(0?[0][1-9]|1?[1][0-2])(0?[0][1-9]|1?[1][0-9]|2?[2][0-9]|3?[3][0-1])[M,H][A-Z]{2}[B,C,D,F,G,H,J,K,L,M,N,Ñ,P,Q,R,S,T,V,W,X,Y,Z]{3}[0-9,A-Z][0-9]/g;
const phoneReg = /^([+]?[(]?[0-9]{2,3}[)]?[-\s.]?)?([0-9]{2})([-\s.]?[0-9]{4}){2}$/;

interface PasswordValid {
  hasNumber: boolean;
  hasCasing: boolean;
  validLength: boolean;
}

export const isPhoneNumber = (numbStr: string): boolean => phoneReg.test(numbStr);

export const isCurpValid = (curp: string): boolean => curpReg.test(curp?.toUpperCase());

export const isEmailValid = (email: string): boolean => {
  return emailReg.test(email?.toLowerCase());
};

export const isPasswordValid = (password: string): PasswordValid => {
  return {
    hasNumber: /[0-9]/.test(password),
    hasCasing: /(?=.*[a-z])(?=.*[A-Z])/.test(password),
    validLength: password.length >= 8,
  };
};

/**
 * Validate card number based on https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s20.html
 * @param number
 */
export const isCardNumberValid = (number: string): boolean => {
  const cardRegex =
    /^(?:(?<visa>4[0-9]{12}(?:[0-9]{3})?)|(?<mastercard>5[1-5][0-9]{14})|(?<amex>3[47][0-9]{13}))$/;
  return cardRegex.test(number.replace(/\s/g, ''));
};

export const isCVVNumberValid = (number: string): boolean => {
  const cvvRegex = /^\d{3,4}$/;
  return cvvRegex.test(number);
};

export const isZipCodeValid = (code: string, locale = 'mx'): boolean => {
  const result = postalCodes.validate(locale, code);
  if (typeof result === 'string') {
    error('error: %s, zipcode: %s', result, code);
    return false;
  }
  return result;
};

export const isBankAccountNumberValid = (number: string): boolean => {
  const regex = new RegExp(/^[0-9]{9,18}$/);
  return regex.test(number.replace(/\s/g, ''));
};

/**
 * @param {string} date MM/YY
 */
export const isValidExpirationDate = (date: string): boolean => {
  date = date.replace(/\s/g, '');
  if (date.length !== 5) return false;

  const now = moment();
  const parseTwoDigitYear = moment.parseTwoDigitYear;

  /**
   * Replacing default parser because moment by default,
   * parse two digit years above 68 are assumed to be in the 1900's and years 68 or below are assumed to be in the 2000's.
   * https://momentjs.com/docs/#/parsing/string-format/
   * @param year
   */
  moment.parseTwoDigitYear = (year: string) => +year + 2000;
  const expiryDate = moment(date, 'MM/YY');
  const referenceDate = moment(`${now.format('MM')}/${now.format('YY')}`, 'MM/YY');

  const result = expiryDate.isValid() && expiryDate.isSameOrAfter(referenceDate);

  // restoring default parser
  moment.parseTwoDigitYear = parseTwoDigitYear;
  return result;
};

export const emailsSeparatedCommas = (emails: string): boolean => {
  const emailsRegex = /^([\w+.%]+@[\w-.]+\.[A-Za-z]{2,8},?)+$/;
  return emailsRegex.test(emails);
};
